import type { ReactNode } from 'react';
import { Camera, Flashlight } from 'lucide-react';
import { StatusBarIcons } from '@/pages/onboarding/mockup/iphone-status-bar';
import lockScreenWallpaper from '@/assets/wallpaper.png';

// Pixel-faithful iOS lock screen: uses Apple's own default lock-screen
// wallpaper (the plain gradient asset, no bezel/widgets baked in — unlike
// `lockscreen_bg.png`, which is itself a whole composed mockup screenshot)
// so the background matches a real device instead of a hand-picked CSS
// gradient. Matches what a real lock screen shows and nothing else — no
// small corner clock (the big widget replaces it, same as real iOS), no
// lock glyph or hint copy (real iOS doesn't show either by default). Used
// as the "before" half of ChooseStoreMockupScene's unlock transition, and
// by the auth backdrop with its own `wallpaper` and notifications as
// `children` (stacked above the flashlight/camera row, as iOS does), and by
// the campaign composer's Korza Studio preview with its own `clock` (it
// rolls to the time a campaign is scheduled for). `dimmed` is the
// always-on display: the wallpaper dims, the clock stays lit.
export function LockScreenScene({
  dateLabel = 'segunda-feira, 6 de outubro',
  clock = '9:41',
  dimmed = false,
  wallpaper,
  children,
}: {
  dateLabel?: ReactNode;
  clock?: ReactNode;
  dimmed?: boolean;
  /** Replaces Apple's default wallpaper; drawn edge to edge behind the UI. */
  wallpaper?: ReactNode;
  children?: ReactNode;
} = {}) {
  return (
    <div
      className="relative flex h-full w-full flex-col bg-cover bg-center text-white"
      style={
        wallpaper
          ? undefined
          : { backgroundImage: `url(${lockScreenWallpaper})` }
      }
    >
      {wallpaper && (
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
          {wallpaper}
        </div>
      )}
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 bg-black transition-opacity ${dimmed ? 'opacity-65 duration-500 ease-out' : 'opacity-0 duration-300 ease-in'}`}
      />
      <StatusBarIcons className="relative" />

      <div className="relative flex flex-col items-center gap-0.5 pt-6">
        <span className="text-[13px] font-medium">{dateLabel}</span>
        <span className="text-7xl font-semibold leading-none tracking-tight">
          {clock}
        </span>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col justify-end">
        {children}
      </div>

      <div
        className={`relative flex items-center justify-between px-6 pb-8 transition-opacity duration-500 ${dimmed ? 'opacity-0' : ''}`}
      >
        <LockScreenButton icon={Flashlight} label="Lanterna" />
        <LockScreenButton icon={Camera} label="Câmera" />
      </div>

      <div className="relative flex justify-center pb-2">
        <div className="h-[5px] w-[134px] rounded-full bg-white/90" />
      </div>
    </div>
  );
}

function LockScreenButton({
  icon: Icon,
  label,
}: {
  icon: typeof Flashlight;
  label: string;
}) {
  return (
    <div
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm"
    >
      <Icon className="h-[18px] w-[18px] text-white" aria-hidden="true" />
    </div>
  );
}
